import type { GradedToken, TokenStatus } from '../lib/grade'
import { parsePinyin } from '../lib/pinyin'

/**
 * Tone contours drawn as actual pitch curves, in the five-level frame Chao
 * Yuen Ren used for his tone letters: 5 is high, 1 is low. The shape is the
 * information — a learner who has seen 214 dip and rise a hundred times reads
 * the third tone faster than they read the digit 3.
 *
 * Coordinates are in a 20x14 box; y is inverted so level 5 sits at the top.
 */
const y = (level: number) => 14 - ((level - 1) / 4) * 12 - 1

const CONTOURS: Record<number, string> = {
  1: `M2,${y(5)} L18,${y(5)}`, // 55  high level
  2: `M2,${y(3)} L18,${y(5)}`, // 35  rising
  3: `M2,${y(2)} Q6,${y(1)} 10,${y(1)} T18,${y(4)}`, // 214 dipping
  4: `M2,${y(5)} L18,${y(1)}`, // 51  falling
}

function ToneMark({ tone }: { tone: number }) {
  const label = tone === 0 ? 'neutral tone' : `tone ${tone}`

  return (
    <svg
      className="ribbon-mark"
      width="20"
      height="14"
      viewBox="0 0 20 14"
      fill="none"
      role="img"
      aria-label={label}
    >
      {tone === 0 ? (
        <circle cx="10" cy={y(3)} r="1.6" fill="currentColor" />
      ) : (
        <path
          d={CONTOURS[tone]}
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          fill="none"
        />
      )}
    </svg>
  )
}

interface Cell {
  tone: number
  label: string
  status: TokenStatus | 'neutral'
}

function Ribbon({ cells }: { cells: Cell[] }) {
  if (!cells.length) return null
  return (
    <div className="ribbon">
      {cells.map((cell, i) => (
        <div key={i} className={`ribbon-cell is-${cell.status}`}>
          <ToneMark tone={cell.tone} />
          <span className="ribbon-syl">{cell.label}</span>
        </div>
      ))}
    </div>
  )
}

/** Reference ribbon: what the block's tones are supposed to do. */
export function ToneReference({ pinyin }: { pinyin: string }) {
  const cells: Cell[] = parsePinyin(pinyin).map((syl) => ({
    tone: syl.tone,
    label: syl.base ? syl.base : syl.raw,
    status: 'neutral',
  }))
  return <Ribbon cells={cells} />
}

/**
 * Scored ribbon: the same contours, coloured by what happened. Extras are
 * dropped — they have no place on the target's contour line.
 */
export function ToneScore({ tokens }: { tokens: GradedToken[] }) {
  const cells: Cell[] = tokens
    .filter((t) => t.status !== 'extra')
    .map((t) => ({
      tone: t.expectedTone,
      label: t.expected || t.got,
      status: t.status,
    }))
  return <Ribbon cells={cells} />
}
